<template>
  <q-card
    :dark="isDarkModeGlobal"
    flat
  >
    <canvas
      id="canvasRef"
      ref="canvasRef"
      width="800"
      height="300"
    />
  </q-card>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {storeToRefs} from "pinia"
const canvasRef = ref(null)
const pulseDur = ref(8)
const rampDur = ref(2)
const pulseRepetitionInterval = ref(10)
const frequency = ref(0.5)
const amplitude = 100
const sampleRate = computed(() => frequency.value * 1000)

import { useCalculatorStore } from '../stores/calculator.store'

const calculatorStore = useCalculatorStore()
const {
  isDarkModeGlobal
} = storeToRefs(calculatorStore)

onMounted(() => {
  drawSoundPulseGraph()
})

watch(isDarkModeGlobal, () => drawSoundPulseGraph())
const axisColor = computed(() => isDarkModeGlobal.value ? 'white' : 'black' )
const textColor = computed(() => isDarkModeGlobal.value ? 'white' : 'black' )
const pulseFillStyle = computed(() => 'red')
const backgroundColor = computed(() => isDarkModeGlobal.value ? '#1d1d1d' : 'white' )
function drawTukey(ctx, width: number, startX: number, startY: number) {
  const alpha = (1 / pulseDur.value) * (rampDur.value * 2)
  ctx.lineWidth = 1.5

  ctx.save()
  ctx.translate(startX, startY)
  ctx.fillStyle = pulseFillStyle.value
  ctx.beginPath()
  // Draw the Tukey window
  let y = 0
  for (let i = 0; i <= width; i++) {

    const x = i / width
    let value = 0
    if (x < alpha / 2) {
        value = 0.5 * (1 + Math.cos(Math.PI * (2 * x / alpha - 1)))
    } else if (x <= 1 - alpha / 2) {
        value = 1
    } else {
        value = 0.5 * (1 + Math.cos(Math.PI * (2 * x / alpha - 2 / alpha + 1)))
    }
    y = value * amplitude

    ctx.lineTo(i, y * Math.cos(2 * Math.PI * i/sampleRate.value * 50))
    ctx.stroke()
  }
  ctx.restore()
  ctx.lineWidth = 1.5
}

function drawAxis(ctx, centerX: number, centerY: number, canvas) {
  ctx.beginPath()
  ctx.strokeStyle = axisColor.value

  // Y-axis
  ctx.moveTo(centerX - 20, centerY - amplitude)
  ctx.lineTo(centerX - 20, centerY + amplitude)
  ctx.stroke()

  // X-axis
  ctx.moveTo(centerX - 20, centerY + amplitude)
  ctx.lineTo(canvas.width / 2, centerY + amplitude)
  ctx.stroke()

  ctx.lineWidth = 1

  // X-axis
  ctx.moveTo(centerX - 20, centerY - amplitude)
  ctx.lineTo(canvas.width / 2, centerY - amplitude)
  ctx.stroke()
}

function drawPulse(ctx, centerX: number, canvas, centerY: number) {
  ctx.beginPath()
  ctx.lineWidth = 1.5

  const maxPulsesToDraw = 1

  // line to connect to pulse
  ctx.strokeStyle = pulseFillStyle.value
  ctx.beginPath()
  ctx.moveTo(centerX, centerY)
  ctx.lineTo(centerX - 20, centerY)
  ctx.stroke()
  ctx.lineWidth = 3
  drawAxis(ctx, centerX, centerY, canvas)
  ctx.lineWidth = 1
  ctx.strokeStyle = pulseFillStyle.value

  // Pulse
  for (let r = 0; r < maxPulsesToDraw; r += 1) {
    ctx.setLineDash([])
    ctx.strokeStyle = pulseFillStyle.value
    ctx.beginPath()

    const chartWidth = canvas.width / 2
    const pulseRepetitionIntervalWidth = chartWidth / maxPulsesToDraw
    const one = pulseRepetitionInterval.value / pulseRepetitionIntervalWidth
    const pulseDurWidth = pulseDur.value / one

    drawTukey(ctx,
        pulseDurWidth,
        centerX + (r * pulseRepetitionIntervalWidth),
        centerY
    )
    ctx.moveTo(centerX + (r * pulseRepetitionIntervalWidth) + pulseDurWidth, centerY)
    ctx.lineTo(centerX + (r * pulseRepetitionIntervalWidth) + pulseRepetitionIntervalWidth, centerY)
    ctx.stroke()
  }
  ctx.lineWidth = 2
}

function drawArrow(ctx, p1, p2, size) {
  const angle = Math.atan2((p2.y - p1.y) , (p2.x - p1.x))
  const hyp = Math.sqrt((p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y))

  ctx.save()
  ctx.translate(p1.x, p1.y)
  ctx.rotate(angle)

  // line
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.lineTo(hyp - size, 0)
  ctx.stroke()

  // triangle
  ctx.beginPath()
  ctx.lineTo(hyp - size, size)
  ctx.lineTo(hyp, 0)
  ctx.lineTo(hyp - size, -size)
  ctx.fill()
  ctx.restore()
}

function drawLabels(ctx, centerX: number, centerY: number, canvas) {
  const canvasWidth = canvas.width
  const arrowPosition = 300
  ctx.fillStyle = textColor.value
  ctx.strokeStyle = textColor.value
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.lineCap = "round"

  ctx.font = "italic 18px roboto"
  ctx.fillText('Pressure Amplitude (PPP)', canvasWidth / 2 + 10, centerY - amplitude)
  ctx.fillText('Peak Rarefaction (PNP)', canvasWidth / 2 + 10, centerY + amplitude)

  ctx.font = "20px roboto"
  ctx.save()
  ctx.translate(70, centerY + amplitude)
  ctx.rotate(-Math.PI/2)
  ctx.fillText('Normalized pressure', 20, 0)
  ctx.restore()

  ctx.font = "italic 20px roboto"
  ctx.fillText('t', arrowPosition, centerY + 150)

  drawArrow(
      ctx,
      { x: arrowPosition - 50, y: centerY + 120 },
      { x: arrowPosition + 50, y: centerY + 120 }, 6
  )
  ctx.font = "12px roboto"
  ctx.lineWidth = 1
  drawTicksX(ctx, centerX, centerY)
}

function drawTicksX(ctx, centerX, centerY) {
  ctx.font = "20px roboto"
  ctx.fillText(' 1.0', 82, centerY - amplitude + 5)
  ctx.fillText(' 0.5', 82, centerY - amplitude / 2  + 5)
  ctx.fillText(' 0.0', 82, centerY  + 5)
  ctx.fillText('-0.5', 82, centerY + amplitude / 2 + 5)
  ctx.fillText('-1.0', 82, centerY + amplitude + 5)

  const startTick = 120
  const endTick = 128

  // line
  ctx.beginPath()
  ctx.moveTo(startTick, centerY - amplitude)
  ctx.lineTo(endTick, centerY - amplitude)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(startTick, centerY - amplitude / 2)
  ctx.lineTo(endTick, centerY - amplitude / 2)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(startTick, centerY)
  ctx.lineTo(endTick, centerY)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(startTick, centerY + amplitude / 2)
  ctx.lineTo(endTick, centerY + amplitude / 2)
  ctx.stroke()

  ctx.beginPath()
  ctx.moveTo(startTick, centerY + amplitude)
  ctx.lineTo(endTick, centerY + amplitude)
  ctx.stroke()

}

const drawSoundPulseGraph = () => {
  const canvas = canvasRef.value
  if (!canvas) return

  canvas.width = 800
  canvas.height = 300

  const centerY = canvas.height / 2
  const ctx = canvas.getContext('2d')

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = backgroundColor.value
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = "white"

  let centerX = 150
  drawLabels(ctx, centerX, centerY, canvas)
  drawPulse(ctx, centerX, canvas, centerY)

}
</script>